import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TSmartVariableDefinition } from '@/types/TSmartCRM';
import {
	createSmartVariable,
	deleteSmartVariable,
	getSmartVariables,
	updateSmartVariable,
} from '@/api/crm/smartCRM';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getSelectedAgentId } from '@/store/account/selector';

export const useSmartVariables = (isNoviUser: boolean) => {
	const queryClient = useQueryClient();
	const selectedAgentId = useAppSelector(getSelectedAgentId);

	const { data, isLoading, error, refetch } = useQuery({
		queryKey: ['smart-variables'],
		queryFn: () => getSmartVariables(isNoviUser),
		select: (response) => response.data.variables || [],
	});

	const createMutation = useMutation({
		mutationKey: ['create-smart-variable'],
		mutationFn: createSmartVariable,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['smart-variables'] });
		},
	});

	const updateMutation = useMutation({
		mutationKey: ['update-smart-variable'],
		mutationFn: updateSmartVariable,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['smart-variables'] });
		},
	});

	const deleteMutation = useMutation({
		mutationKey: ['delete-smart-variable'],
		mutationFn: deleteSmartVariable,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['smart-variables'] });
		},
	});

	const saveField = async (fieldData: Partial<TSmartVariableDefinition>) => {
		const payload = {
			internalKey: fieldData.internalKey,
			fieldName: fieldData.fieldName,
			fieldType: fieldData.fieldType,
			options: fieldData.options,
			description: fieldData.description,
			isRequired: fieldData.isRequired,
		};

		if (fieldData.id) {
			return updateMutation.mutateAsync({
				variableId: fieldData.id,
				...payload,
			});
		} else {
			return createMutation.mutateAsync({
				agentId: selectedAgentId,
				...payload,
			});
		}
	};

	const deleteField = async (fieldId: string) => {
		return deleteMutation.mutateAsync(fieldId);
	};

	return {
		fields: data || [],
		loading: isLoading,
		error,
		saveField,
		deleteField,
		refetch,
		isSaving: createMutation.isPending || updateMutation.isPending,
		isDeleting: deleteMutation.isPending,
	};
};
